/**
 * Facing walls and area overlap (PRP-SKETCH-FIELD-2 E2, E3, E6). Pure geometry.
 */
import { POLICY } from './policy';
import { EPS, area, areaPts, axisOf, inPolyPts, outN } from './geom';
import type { Axis, Corner, Pt, Room } from './types';

export interface Facing {
  /** Wall a1 -> a2 of room A faces wall b1 -> b2 of room B. */
  a1: Corner; a2: Corner; b1: Corner; b2: Corner;
  /** Gap between the two faces, feet (positive: apart). */
  g: number;
  /** Overlap along the walls, feet. */
  overlap: number;
}

/** Every pair of facing straight walls of A and B overlapping by at least `minOverlap` feet. */
export function facingWalls(A: Room, B: Room, minOverlap: number): Facing[] {
  const out: Facing[] = [];
  const aA = area(A), aB = area(B);
  A.corners.forEach((a1, ia) => {
    const a2 = A.corners[(ia + 1) % A.corners.length], t = axisOf(a1, a2);
    if (t !== 'h' && t !== 'v') return;
    const nA = outN(A, a1, a2, aA);
    const lx: Axis = t === 'h' ? 'y' : 'x', ox: Axis = t === 'h' ? 'x' : 'y';
    B.corners.forEach((b1, ib) => {
      const b2 = B.corners[(ib + 1) % B.corners.length];
      if (axisOf(b1, b2) !== t) return;
      const nB = outN(B, b1, b2, aB);
      if (nA.x * nB.x + nA.y * nB.y > POLICY.facing_dot_max) return;
      const lo = Math.max(Math.min(a1[ox], a2[ox]), Math.min(b1[ox], b2[ox]));
      const hi = Math.min(Math.max(a1[ox], a2[ox]), Math.max(b1[ox], b2[ox]));
      if (hi - lo < minOverlap) return;
      out.push({ a1, a2, b1, b2, g: (b1[lx] - a1[lx]) * nA[lx], overlap: hi - lo });
    });
  });
  return out;
}

const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

/** Segments p1p2 and q1q2 cross at a point inside both (touching does not count). */
function properCross(p1: Pt, p2: Pt, q1: Pt, q2: Pt): boolean {
  const d1 = cross(q1, q2, p1), d2 = cross(q1, q2, p2), d3 = cross(p1, p2, q1), d4 = cross(p1, p2, q2);
  return ((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS)) && ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS));
}

/**
 * Two simple polygons share area (not just an edge or a corner). Exact for
 * square-cornered outlines: every cell of the grid through both outlines'
 * coordinates is tested at its centre; a proper edge crossing catches angled walls.
 */
export function polysOverlap(P: Pt[], Q: Pt[]): boolean {
  if (Math.abs(areaPts(P)) < EPS || Math.abs(areaPts(Q)) < EPS) return false;
  for (let i = 0; i < P.length; i++) {
    for (let j = 0; j < Q.length; j++) {
      if (properCross(P[i], P[(i + 1) % P.length], Q[j], Q[(j + 1) % Q.length])) return true;
    }
  }
  return sharedCells(P, Q, true).length > 0;
}

interface Cell { x0: number; y0: number; x1: number; y1: number }

/** Cells of the grid through both outlines' coordinates whose centre is inside both (`first`: stop at one). */
function sharedCells(P: Pt[], Q: Pt[], first = false): Cell[] {
  const uniq = (v: number[]) => [...new Set(v.map((x) => Math.round(x / EPS) * EPS))].sort((a, b) => a - b);
  const xs = uniq([...P, ...Q].map((p) => p.x)), ys = uniq([...P, ...Q].map((p) => p.y));
  const out: Cell[] = [];
  for (let i = 0; i + 1 < xs.length; i++) {
    for (let j = 0; j + 1 < ys.length; j++) {
      const c = { x: (xs[i] + xs[i + 1]) / 2, y: (ys[j] + ys[j + 1]) / 2 };
      if (inPolyPts(P, c) && inPolyPts(Q, c)) {
        out.push({ x0: xs[i], y0: ys[j], x1: xs[i + 1], y1: ys[j + 1] });
        if (first) return out;
      }
    }
  }
  return out;
}

/**
 * PRP-WEB-2 W1.5: how deep two outlines overlap, feet: the shorter side of the box
 * around their shared area, which is the distance that would nudge two rectangles
 * apart. 0 when they share no area. Exact for square-cornered outlines.
 */
export function overlapDepth(P: Pt[], Q: Pt[]): number {
  if (Math.abs(areaPts(P)) < EPS || Math.abs(areaPts(Q)) < EPS) return 0;
  const cells = sharedCells(P, Q);
  if (!cells.length) return 0;
  const x0 = Math.min(...cells.map((c) => c.x0)), y0 = Math.min(...cells.map((c) => c.y0));
  const x1 = Math.max(...cells.map((c) => c.x1)), y1 = Math.max(...cells.map((c) => c.y1));
  return Math.min(x1 - x0, y1 - y0);
}
